import type { DataProvider } from "ra-core";

import type { Tag } from "../types";
import { TAG_FETCH_LIMIT } from "./useAllTags";

// Turns a list of tag names into tag records, creating only the ones that do not exist.
// Both importers need this and neither did it correctly:
//
//   * the CSV importer matched with `name@in`, an exact comparison, so "Main Prospects"
//     and "main prospects" were two different tags;
//   * the JSON importer never queried the database at all, so every name in a payload
//     was created fresh with a random colour.
//
// Since tags.name carries a unique index on lower(btrim(name)), both of those now fail
// on insert rather than quietly making duplicates.
//
// Matching happens in memory rather than through a filter. PostgREST offers `ilike`, but
// `transformFilter` translates only ten operators for the FakeRest provider and that is
// not one of them, and AGENTS.md requires any operator used to work on both providers.
// Case-insensitive matching against a list is awkward to express as a filter anyway. Tags
// are a small closed vocabulary, so fetching them and comparing here is both simpler and
// identical across providers.
export type TagCache = Map<string, Tag>;

// The database key: whatever the unique index normalises on. Keep these in step.
const keyOf = (name: string) => name.trim().toLowerCase();

export async function resolveTagsByName({
  dataProvider,
  names,
  cache,
  colorForNewTag,
}: {
  dataProvider: DataProvider;
  names: string[];
  // Caller-owned and keyed by `keyOf`, so a multi-batch import does not refetch, and a
  // tag created in one batch is visible to the next.
  cache: TagCache;
  colorForNewTag: (name: string) => string;
}): Promise<Map<string, Tag>> {
  const requested = [
    ...new Set(names.map((name) => name.trim()).filter(Boolean)),
  ];
  if (requested.length === 0) return new Map();

  // One fetch covers every miss. The old code fetched only the names it lacked, which
  // was more requests for no benefit at this size.
  if (requested.some((name) => !cache.has(keyOf(name)))) {
    const { data } = await dataProvider.getList<Tag>("tags", {
      filter: {},
      pagination: { page: 1, perPage: TAG_FETCH_LIMIT },
      sort: { field: "name", order: "ASC" },
    });
    for (const tag of data) cache.set(keyOf(tag.name), tag);
  }

  // Deduplicate by the database's key before creating, not by the raw name. A single file
  // holding both "Apple" and "apple" would otherwise race two inserts and one would take
  // a unique violation.
  const toCreate = new Map<string, string>();
  for (const name of requested) {
    const key = keyOf(name);
    if (!cache.has(key) && !toCreate.has(key)) toCreate.set(key, name);
  }

  await Promise.all(
    [...toCreate.values()].map(async (name) => {
      const { data } = await dataProvider.create<Tag>("tags", {
        data: { name, color: colorForNewTag(name) },
      });
      cache.set(keyOf(data.name), data);
    }),
  );

  // Keyed by the name the caller asked for, so call sites look up with their own string
  // and never have to know about normalisation.
  return requested.reduce((acc, name) => {
    const tag = cache.get(keyOf(name));
    if (tag) acc.set(name, tag);
    return acc;
  }, new Map<string, Tag>());
}
